import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { getLocalStorageBaseUrl } from 'utils/localStorage';

const StompContext = createContext(null);

export const StompProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const subscriptions = useRef(new Map());

  useEffect(() => {
    const socketFactory = () => new SockJS(`${getLocalStorageBaseUrl()}/websocket`);

    const stompClient = new Client({
      webSocketFactory: socketFactory,
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      debug: (str) => console.log('[STOMP]', str),
      onConnect: () => {
        console.log('✅ STOMP Connected');
        setIsConnected(true);
      },
      onDisconnect: () => {
        console.log('🔌 STOMP Disconnected');
        setIsConnected(false);
      },
      onStompError: (frame) => {
        console.error('❌ STOMP Error:', frame.headers['message'], frame.body);
      },
    });

    stompClient.activate();
    setClient(stompClient);

    return () => {
      stompClient.deactivate();
      setIsConnected(false);
    };
  }, []);

  const subscribe = (destination, callback) => {
    if (!client || !isConnected) {
      console.warn('⚠️ STOMP not connected yet.');
      return null;
    }

    const subscription = client.subscribe(destination, (message) => {
      try {
        const body = JSON.parse(message.body);
        callback(body);
      } catch {
        console.warn('Invalid message body:', message.body);
      }
    });

    subscriptions.current.set(destination, subscription);
    console.log('🟢 Subscribed to:', destination);
    return subscription;
  };

  const unsubscribe = (destination) => {
    const subscription = subscriptions.current.get(destination);
    if (subscription) {
      subscription.unsubscribe();
      subscriptions.current.delete(destination);
      console.log('🔴 Unsubscribed from:', destination);
    }
  };

  return (
    <StompContext.Provider value={{ client, isConnected, subscribe, unsubscribe }}>
      {children}
    </StompContext.Provider>
  );
};

export const useStomp = () => useContext(StompContext);
